import * as Sentry from "@sentry/react";
import { aiDryRun } from "./env";
import { amendUrlWithQuery } from "./browser";
import { handleError } from "./errorHandling";
import { fetchAi } from "../aiApi";

const MAX_BODY_SNIPPET = 2000;

const generateRequestId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ai-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const captureAiStreamIssue = (message, { level = "error", tags, extras }) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (tags) {
      Object.entries(tags).forEach(([k, v]) => scope.setTag(k, v));
    }
    if (extras) {
      Object.entries(extras).forEach(([k, v]) => scope.setExtra(k, v));
    }
    Sentry.captureException(
      message instanceof Error ? message : new Error(String(message))
    );
  });
};

/**
 * Generic handler for streaming API responses using Server-Sent Events (SSE)
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - The API endpoint to call
 * @param {Object} options.body - The request body
 * @param {?AbortSignal} options.signal - AbortSignal
 * @param {Function} options.onProgress - Callback for progress updates
 * @param {Function} options.onError - Callback for error handling
 * @param {Function} options.onComplete - Callback for completion
 * @returns {Promise<void>}
 */
export const handleAiStreamingResponse = async ({
  endpoint,
  body,
  signal,
  onProgress,
  onError,
  onComplete,
  isTtgUser,
}) => {
  const requestId = generateRequestId();
  const startedAt = performance.now();
  const elapsed = () => Math.round(performance.now() - startedAt);

  const baseTags = {
    "ai.request_id": requestId,
    "ai.endpoint": endpoint,
  };

  try {
    let url = endpoint;
    if (aiDryRun) {
      // For faster iteration with development if we don't care about
      // the accuracy of responses.
      url = amendUrlWithQuery(url, { "dry-run": "1" });
    }
    const response = await fetchAi(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "X-Request-Id": requestId,
      },
      body: JSON.stringify(body),
      signal,
    });

    // Create an EventSource-like reader using the fetch API
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages from the buffer
      const messages = buffer.split("\n\n");
      buffer = messages.pop() || ""; // Keep the last incomplete message in the buffer

      for (const message of messages) {
        if (!message.trim()) continue;

        const lines = message.split("\n");
        const parsedMessage = {};

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            parsedMessage.type = line.slice(7);
          } else if (line.startsWith("data: ")) {
            parsedMessage.data = line.slice(6);
          }
        }

        const { type, data } = parsedMessage;

        if (type && data) {
          try {
            // show error message in AI chat
            if (type === "error") {
              captureAiStreamIssue(
                `AI stream SSE error event: ${String(data).slice(0, 200)}`,
                {
                  tags: { ...baseTags, "ai.failure_class": "sse_error_event" },
                  extras: {
                    elapsed_ms: elapsed(),
                    sse_data: String(data).slice(0, MAX_BODY_SNIPPET),
                  },
                }
              );
              return onError?.(
                isTtgUser ? String(data).replaceAll('"', "") : standartMessage
              );
            }

            // For content and other data types that might be JSON
            const parsed = JSON.parse(data);
            onProgress?.({ type: parsedMessage.type, data: parsed });
          } catch (e) {
            // If it's not JSON, send the raw data
            onProgress?.({ type, data });
          }
        }
      }
    }

    // show error message in AI chat
    if (!response.ok) {
      const responseHeaders = {};
      try {
        response.headers.forEach((v, k) => {
          responseHeaders[k] = v;
        });
      } catch {
        // Headers iteration unsupported; skip.
      }
      captureAiStreamIssue(
        `AI stream non-OK response: ${response.status} ${response.statusText}`,
        {
          tags: {
            ...baseTags,
            "ai.failure_class": "http_not_ok",
            "ai.status": String(response.status),
          },
          extras: {
            status: response.status,
            status_text: response.statusText,
            elapsed_ms: elapsed(),
            body_snippet: String(buffer).slice(0, MAX_BODY_SNIPPET),
            response_headers: responseHeaders,
          },
        }
      );
      if (isTtgUser) {
        const { detail } = JSON.parse(String(buffer)) ?? {};
        return onError?.(detail);
      }

      return onError?.(standartMessage);
    }

    onComplete?.();
  } catch (error) {
    if (error.name === "AbortError") {
      onComplete?.();
    } else {
      Sentry.withScope((scope) => {
        scope.setTag("ai.request_id", requestId);
        scope.setTag("ai.endpoint", endpoint);
        scope.setTag("ai.failure_class", "fetch_exception");
        scope.setExtra("elapsed_ms", elapsed());
        scope.setExtra("error_name", error?.name);
        scope.setExtra("error_message", error?.message);
        handleError(error);
      });
      onError?.(standartMessage);
    }
  }
};

const standartMessage =
  "There has been an error with the model provider. Please try again";
